import React, { type PropsWithChildren, type ReactNode } from "react";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button, { type ButtonProps } from "@material-ui/core/Button";
import Tooltip, { type TooltipProps } from "@material-ui/core/Tooltip";

import type { Theme } from "src/theme";
import { useIsFullscreen } from "src/utils/fullscreen";

const useButtonStyles = makeStyles((theme) => ({
  errorButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

export interface TableHeaderButtonProps extends ButtonProps {
  tooltip?: TooltipProps["title"];
  Component?: typeof Button;
  colorType?: "error";
}

export function TableHeaderButton({
  tooltip,
  disabled,
  Component = Button,
  colorType,
  className,
  color,
  ...props
}: TableHeaderButtonProps) {
  const classes = useButtonStyles();
  const isError = colorType === "error";

  const button = (
    <Component
      variant="contained"
      disabled={disabled}
      className={clsx(className, isError && classes.errorButton)}
      color={isError ? undefined : color}
      {...props}
    />
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        {disabled ? <span>{button}</span> : button}
      </Tooltip>
    );
  }

  return button;
}

const useTitleStyles = makeStyles((theme: Theme) => {
  const { derived } = theme.palette.custom;

  return {
    root: {
      color: derived.titleOnCard,
      fontWeight: theme.typography.fontWeightBold,
    },
  };
});

export function Title({ children }: PropsWithChildren<{}>) {
  const classes = useTitleStyles();
  return (
    <Typography variant="h6" component="h2" className={classes.root}>
      {children}
    </Typography>
  );
}

export interface TableHeaderProps {
  title: ReactNode;
  hideChildrenInFullscreen?: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  const { derived } = theme.palette.custom;

  return {
    title: {
      color: derived.titleOnCard,
      fontWeight: theme.typography.fontWeightBold,
    },
    container: {
      display: "flex",
      alignItems: "center",
      "& > * + *": {
        marginLeft: theme.spacing(1),
      },
    },
  };
});

function TableHeader({
  title,
  children,
  hideChildrenInFullscreen = false,
}: PropsWithChildren<TableHeaderProps>) {
  const classes = useStyles();
  const hideButtons = useIsFullscreen() && hideChildrenInFullscreen;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      flexWrap="wrap"
      alignItems="center"
      mb={1.5}
    >
      <Title>{title}</Title>
      {children && !hideButtons ? (
        <div className={classes.container}>{children}</div>
      ) : null}
    </Box>
  );
}

export default TableHeader;
